import {
  type FC, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { useBoundaryTerritoryMetricsData } from '~/_shared/utils/metric/useBoundaryTerritoryMetricsData';
import { MapPageSplitScreenTab } from '~/map/page/splitScreen/mapPageSplitScreen.types';
import { useBoundaryBatchEditLocations } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryBatchEditLocations';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import { isDataLayersArtificialBoundaryTerritoryGroup } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import { useBoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  useBoundaryTerritoryAssignments, useBoundaryTerritoryDetailsIsLoadingSelector,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  deactivateActiveBoundary, setSpreadsheetPreviewFilterState,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { weightedCenterSetSelectedEntities } from '~/store/frontendState/mapTools/weightedCenter/weightedCenter.actionCreators';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '~/store/selectors/useMapInfoSelectors';
import { useSplitPanelState } from '~/store/userData/settings/splitPanel/useSplitPanelState';
import { useModal } from '../../../../modal/useModal.hook';
import { useBoundaryDirections } from '../../mapTools/boundary/hooks/useBoundaryDirections';
import { useBoundaryExportData } from '../../mapTools/boundary/hooks/useBoundaryExportData';
import { useRemoveBoundaryTerritory } from '../../mapTools/boundary/hooks/useRemoveBoundaryTerritory';
import { BoundaryDetailsComponent } from '../boundaryDetails/boundaryDetails.component';
import { useBoundaryDetailsMarkersCount } from '../boundaryDetails/useBoundaryDetailsMarkersCount';

type BoundaryTerritoryDetailsContainerProps = {
  boundaryTerritoryId: string;
  boundaryTerritoryGroupId: number;

  onClose: () => void;
};

export const BoundaryTerritoryDetailsContainer: FC<BoundaryTerritoryDetailsContainerProps> = (props) => {
  const dispatch = useDispatch();
  const mapInfo = useMapInfoDataSelector();
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const publicMapSettings = usePublicMapSettingsSelector();
  const isBoundaryTerritoryDetailsLoading = useBoundaryTerritoryDetailsIsLoadingSelector();
  const { getBoundaryTerritoryDisplayName } = useGetBoundaryTerritoryDisplayNames();
  const { exportBoundaryTerritoryData } = useBoundaryExportData();
  const { removeTerritory } = useRemoveBoundaryTerritory();
  const { batchEditLocationsInBoundaryTerritory } = useBoundaryBatchEditLocations();
  const { addBoundaryTerritoryDirections } = useBoundaryDirections();
  const isMapPresentational = useIsMapPresentationalSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { openModal: openBoundaryCustomizeMetricsModal } = useModal('BoundaryCustomizeMetrics');
  const { openModal: openExportContainedBoundaryDataModal } = useModal('ExportContainedBoundaryData');
  const isMobileScreen = useIsMobileScreenSelector();
  const { openPanelForCurrentMap: openSplitScreenPanel } = useSplitPanelState(null);

  const boundaryTerritoryGroup: BoundaryTerritoryGroup | null = getBoundaryTerritoryGroupById(props.boundaryTerritoryGroupId) ?? null;

  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup?.boundaryGroupId);

  const boundaryTerritory: BoundaryTerritory | null = useMemo(() => {
    const boundaryTerritory = boundaryTerritoryGroup?.settings.boundaryTerritories
      .find(territory => territory.boundaryTerritoryId === props.boundaryTerritoryId);

    return boundaryTerritory ?? null;
  }, [boundaryTerritoryGroup?.settings.boundaryTerritories, props.boundaryTerritoryId]);

  const { metricsResults, isLoading } = useBoundaryTerritoryMetricsData(
    props.boundaryTerritoryId,
    props.boundaryTerritoryGroupId,
    boundaryTerritoryGroup?.settings.ignoreFilters,
  );

  const closeActiveBoundary = useCallback(() => {
    dispatch(deactivateActiveBoundary());
  }, [dispatch]);

  useEffect(() => {
    if (!boundaryTerritory) {
      closeActiveBoundary();
    }
  });

  const boundaryIds = useMemo(() => {
    const boundaryIdsSet = boundaryTerritoryAssignments.get(props.boundaryTerritoryGroupId)
      ?.territoryAssignments.get(props.boundaryTerritoryId);

    if (!boundaryIdsSet) {
      return [];
    }

    return Array.from(boundaryIdsSet);
  }, [boundaryTerritoryAssignments, props.boundaryTerritoryGroupId, props.boundaryTerritoryId]);

  const { markersCount } = useBoundaryDetailsMarkersCount(props.boundaryTerritoryGroupId, boundaryIds);

  const previousBoundaryTerritory = usePrevious(boundaryTerritory);

  const onExportDataClick = isBoundaryExportLocationsAllowed
    ? () => exportBoundaryTerritoryData(props.boundaryTerritoryGroupId, props.boundaryTerritoryId)
    : undefined;

  const onExportContainedBoundariesClick = !isMapPresentational
    ? () => openExportContainedBoundaryDataModal({
      filter: {
        type: 'boundary_territory',
        boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
        boundaryTerritoryIds: [props.boundaryTerritoryId],
      },
    }) : undefined;

  const onRemoveBTClick = (boundaryTerritory?.custom && !isEditModeDisabled)
    ? () => removeTerritory(props.boundaryTerritoryGroupId, props.boundaryTerritoryId)
    : undefined;

  const onBatchEditLocations = !isEditModeDisabled && !checkIsSalesforceMap(mapInfo)
    ? () => batchEditLocationsInBoundaryTerritory(props.boundaryTerritoryGroupId, props.boundaryTerritoryId)
    : undefined;

  const onDirectionsClick = !isMapPresentational || publicMapSettings.routingDirections ?
    () => addBoundaryTerritoryDirections(props.boundaryTerritoryGroupId, props.boundaryTerritoryId)
    : undefined;

  const entityFilter = useMemo(() => boundaryTerritoryGroup ? ({
    boundaryTerritories: [{
      boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
      boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
      boundaryTerritoryId: props.boundaryTerritoryId,
    }],
  }) : undefined, [boundaryTerritoryGroup, props.boundaryTerritoryGroupId, props.boundaryTerritoryId]);

  const onPreviewSpreadsheetData = boundaryTerritoryGroup && entityFilter && !isMapPresentational && !isMobileScreen ?
    () => {
      dispatch(setSpreadsheetPreviewFilterState(entityFilter));
      openSplitScreenPanel(MapPageSplitScreenTab.Spreadsheet);
    }
    : undefined;

  const onShowWeightedCenter = boundaryTerritoryGroup && entityFilter && (markersCount || 0) > 0 && !isMapPresentational && !isMobileScreen
    ? () => {
      dispatch(weightedCenterSetSelectedEntities(entityFilter));
    } : undefined;

  const isMetricsEditAllowed = !isEditModeDisabled && boundaryTerritoryGroup && !isDataLayersArtificialBoundaryTerritoryGroup(boundaryTerritoryGroup);

  const onEditMetrics = useMemo(() => isMetricsEditAllowed ? () => openBoundaryCustomizeMetricsModal({
    boundaryTerritoryGroupId: props.boundaryTerritoryGroupId,
    isBoundaryGroupCustom: boundaryPermissions.boundaryIsCustom,
  }) : undefined, [boundaryPermissions.boundaryIsCustom, isMetricsEditAllowed, openBoundaryCustomizeMetricsModal, props.boundaryTerritoryGroupId]);

  useEffect(() => {
    if (previousBoundaryTerritory && !boundaryTerritory) {
      closeActiveBoundary();
    }
  }, [previousBoundaryTerritory, boundaryTerritory, closeActiveBoundary]);

  return (
    <BoundaryDetailsComponent
      boundaryName={getBoundaryTerritoryDisplayName(props.boundaryTerritoryId, props.boundaryTerritoryGroupId)}
      boundaryPermissions={boundaryPermissions}
      isArtificial={false}
      isMobileVersion={isMobileScreen}
      isEditModeDisabled={isEditModeDisabled}
      boundaryPolygonsTotal={0}
      isEditingBoundary={false}
      isLoading={isLoading || isBoundaryTerritoryDetailsLoading}
      markersCount={markersCount}
      metrics={metricsResults}
      ignoreFilters={!!boundaryTerritoryGroup?.settings.ignoreFilters}
      onClose={closeActiveBoundary}
      onExportLocationsClick={onExportDataClick}
      onExportContainedBoundariesClick={onExportContainedBoundariesClick}
      onBoundaryItemRemoveClick={onRemoveBTClick}
      onBatchEditLocationsClick={onBatchEditLocations}
      onDirectionsClick={onDirectionsClick}
      isLayeredMapConnected={isLayeredMapConnected}
      onCustomizeMetricsClick={onEditMetrics}
      onPreviewSpreadsheetData={onPreviewSpreadsheetData}
      onShowWeightedCenter={onShowWeightedCenter}
    />
  );
};
